<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useMutation } from "@urql/vue";
import gql from "graphql-tag";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import {
  TeamDetailsByIdDocument,
  useMyAdminTeamsShortQuery,
} from "@/graphql/operations";
import graphqlClient from "@/graphql/client";
import { isActiveAdminTeam } from "@/views/messages/messagesAccess";

interface TeamOption {
  id: string;
  name: string;
}

interface MemberOption {
  id: string;
  name: string;
  email: string;
  teamNames: string[];
}

interface SendTeamMessageMutationResult {
  pvSendTeamMessage?: {
    clientMutationId?: string | null;
  } | null;
}

interface SendTeamMessageMutationVariables {
  teamIds: string[];
  recipientUserIds?: string[] | null;
  messageText: string;
  includeSender: boolean;
}

const SendTeamMessageDocument = gql`
  mutation SendTeamMessage(
    $teamIds: [BigInt!]!
    $recipientUserIds: [BigInt!]
    $messageText: String!
    $includeSender: Boolean!
  ) {
    pvSendTeamMessage(
      input: {
        teamIds: $teamIds
        recipientUserIds: $recipientUserIds
        messageText: $messageText
        includeSender: $includeSender
      }
    ) {
      clientMutationId
    }
  }
`;

const { t } = useI18n();
const router = useRouter();
const { data: adminTeamsData, fetching: adminTeamsFetching } =
  useMyAdminTeamsShortQuery({
    requestPolicy: "cache-and-network",
  });
const {
  executeMutation: sendTeamMessageMutation,
  fetching: isSendingMessage,
} = useMutation<
  SendTeamMessageMutationResult,
  SendTeamMessageMutationVariables
>(SendTeamMessageDocument);

const selectedTeamIds = ref<string[]>([]);
const selectedRecipientIds = ref<string[]>([]);
const includeSender = ref(false);
const messageText = ref("");
const membersLoading = ref(false);
const recipientsCollapsed = ref(true);
const submitError = ref("");
const submitSuccess = ref("");
const membersRequestRun = ref(0);

const adminTeams = computed<TeamOption[]>(() => {
  const items = adminTeamsData.value?.myAdminTeams?.nodes ?? [];
  return items
    .filter(isActiveAdminTeam)
    .map((team) => ({
      id: team.id,
      name: team.name,
    }))
    .sort((left, right) => left.name.localeCompare(right.name));
});

const members = ref<MemberOption[]>([]);
const hasAdminTeams = computed(() => adminTeams.value.length > 0);
const trimmedMessageText = computed(() => messageText.value.trim());
const canSend = computed(() => {
  if (!trimmedMessageText.value) return false;
  if (!selectedTeamIds.value.length) return false;
  return selectedRecipientIds.value.length > 0 || includeSender.value;
});

watch(
  [adminTeamsFetching, adminTeams],
  ([fetching, teams]) => {
    if (!fetching && teams.length === 0) {
      router.replace("/404");
    }
  },
  { immediate: true }
);

watch(
  adminTeams,
  (teams) => {
    if (!teams.length) {
      selectedTeamIds.value = [];
      return;
    }

    if (!selectedTeamIds.value.length) {
      selectedTeamIds.value = [teams[0].id];
      return;
    }

    const teamIds = new Set(teams.map((team) => team.id));
    selectedTeamIds.value = selectedTeamIds.value.filter((id) =>
      teamIds.has(id)
    );
  },
  { immediate: true }
);

watch(
  selectedTeamIds,
  async () => {
    await loadMembersForSelectedTeams();
  },
  { immediate: true }
);

watch(
  members,
  () => {
    recipientsCollapsed.value = true;
  },
  { immediate: true }
);

async function loadMembersForSelectedTeams() {
  const requestId = membersRequestRun.value + 1;
  membersRequestRun.value = requestId;
  submitError.value = "";

  if (!selectedTeamIds.value.length) {
    members.value = [];
    selectedRecipientIds.value = [];
    return;
  }

  membersLoading.value = true;
  try {
    const selectedTeams = new Map(
      adminTeams.value.map((team) => [team.id, team.name])
    );
    const responses = await Promise.all(
      selectedTeamIds.value.map((teamId) =>
        graphqlClient
          .query(
            TeamDetailsByIdDocument,
            { id: teamId },
            { requestPolicy: "network-only" }
          )
          .toPromise()
      )
    );

    if (membersRequestRun.value !== requestId) {
      return;
    }

    const memberMap = new Map<string, MemberOption>();
    for (const response of responses) {
      if (response.error) {
        throw response.error;
      }

      const team = response.data?.team;
      if (!team) {
        continue;
      }

      const teamName = selectedTeams.get(team.id) ?? team.name;
      for (const membership of team.activeUserMemberships.nodes ?? []) {
        const user = membership?.user;
        if (!user?.id) {
          continue;
        }

        const existing = memberMap.get(user.id);
        if (existing) {
          if (!existing.teamNames.includes(teamName)) {
            existing.teamNames.push(teamName);
            existing.teamNames.sort((left, right) =>
              left.localeCompare(right)
            );
          }
          continue;
        }

        memberMap.set(user.id, {
          id: user.id,
          email: user.email,
          name: user.displayName || user.email,
          teamNames: [teamName],
        });
      }
    }

    members.value = Array.from(memberMap.values()).sort((left, right) =>
      left.name.localeCompare(right.name)
    );
    selectedRecipientIds.value = members.value.map((member) => member.id);
  } catch (error: any) {
    submitError.value = error?.message || t("messagesPage.errors.loadMembers");
    members.value = [];
    selectedRecipientIds.value = [];
  } finally {
    if (membersRequestRun.value === requestId) {
      membersLoading.value = false;
    }
  }
}

function selectAllRecipients() {
  selectedRecipientIds.value = members.value.map((member) => member.id);
}

function clearRecipients() {
  selectedRecipientIds.value = [];
}

function toggleRecipientsCollapsed() {
  recipientsCollapsed.value = !recipientsCollapsed.value;
}

async function submit() {
  submitError.value = "";
  submitSuccess.value = "";

  if (!canSend.value) {
    return;
  }

  const recipientUserIds =
    selectedRecipientIds.value.length === members.value.length
      ? null
      : selectedRecipientIds.value;

  const result = await sendTeamMessageMutation({
    teamIds: selectedTeamIds.value,
    recipientUserIds,
    messageText: trimmedMessageText.value,
    includeSender: includeSender.value,
  });

  if (result.error) {
    submitError.value = result.error.message;
    return;
  }

  submitSuccess.value = t("messagesPage.form.success");
  messageText.value = "";
}
</script>

<template>
  <div class="page_content">
    <h3>{{ $t("messagesPage.title") }}</h3>

    <section>
      <h4>{{ $t("messagesPage.teams.title") }}</h4>
      <p class="info">{{ $t("messagesPage.teams.info") }}</p>
      <template v-if="adminTeamsFetching && !hasAdminTeams">
        <div class="loading_status">
          <LoadingSpinner color="var(--classic-text, black)" />
        </div>
      </template>
      <template v-else-if="!hasAdminTeams">
        <div class="info">{{ $t("messagesPage.noAdminTeams") }}</div>
      </template>
      <div v-else class="checklist">
        <label v-for="team in adminTeams" :key="team.id" class="item">
          <input type="checkbox" :value="team.id" v-model="selectedTeamIds" />
          <span>{{ team.name }}</span>
        </label>
      </div>
    </section>

    <section>
      <div class="title_row">
        <h4>{{ $t("messagesPage.recipients.title") }}</h4>
        <div class="controls">
          <button
            v-if="members.length > 0"
            class="button secondary small toggle_button"
            @click="toggleRecipientsCollapsed"
          >
            <span class="chevron" :class="{ expanded: !recipientsCollapsed }">
              ▸
            </span>
            {{
              recipientsCollapsed
                ? $t("messagesPage.recipients.showList", {
                    count: members.length,
                  })
                : $t("messagesPage.recipients.hideList")
            }}
          </button>
          <button class="button secondary small" @click="selectAllRecipients">
            {{ $t("messagesPage.recipients.selectAll") }}
          </button>
          <button class="button secondary small" @click="clearRecipients">
            {{ $t("messagesPage.recipients.clearAll") }}
          </button>
        </div>
      </div>
      <p class="info">{{ $t("messagesPage.recipients.info") }}</p>

      <template v-if="membersLoading">
        <div class="loading_status">
          <LoadingSpinner color="var(--classic-text, black)" />
        </div>
      </template>
      <template v-else-if="members.length === 0">
        <div class="info">{{ $t("messagesPage.recipients.empty") }}</div>
      </template>
      <div v-else-if="recipientsCollapsed" class="info">
        {{ $t("messagesPage.recipients.listHidden", { count: members.length }) }}
      </div>
      <div v-else class="checklist">
        <label v-for="member in members" :key="member.id" class="item">
          <input
            type="checkbox"
            :value="member.id"
            v-model="selectedRecipientIds"
          />
          <span class="member_name">{{ member.name }}</span>
          <span class="member_email">({{ member.email }})</span>
          <span class="member_teams">
            {{ member.teamNames.join(", ") }}
          </span>
        </label>
      </div>
    </section>

    <section>
      <h4>{{ $t("messagesPage.form.title") }}</h4>
      <textarea
        v-model="messageText"
        class="message_input"
        :maxlength="2000"
        :placeholder="$t('messagesPage.form.placeholder')"
      />
      <div class="field_row">
        <label class="item">
          <input type="checkbox" v-model="includeSender" />
          {{ $t("messagesPage.form.includeSender") }}
        </label>
      </div>

      <p v-if="submitError" class="status error">{{ submitError }}</p>
      <p v-if="submitSuccess" class="status success">{{ submitSuccess }}</p>

      <div class="buttons">
        <button
          :class="{ button: true, primary: true, disabled: !canSend || isSendingMessage }"
          @click="submit"
        >
          {{ $t("messagesPage.form.send") }}
        </button>
      </div>
    </section>
  </div>
</template>

<style scoped>
h3 {
  font-size: 1.1rem;
  font-weight: 500;
  margin-top: 0;
}

h4 {
  font-weight: 500;
  margin: 0;
  line-height: 2.2;
}

section {
  border-bottom: 1px solid var(--classic-border-subtle, #e0e0e0);
  margin-bottom: 1.5em;
  padding-bottom: 1em;
}

.page_content section:last-child {
  border-bottom: none;
}

.info {
  font-size: 0.9em;
  margin-bottom: 0.6em;
}

.loading_status {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
}

.checklist {
  max-height: 280px;
  overflow-y: auto;
  border: 1px solid var(--classic-border-subtle, #e0e0e0);
  border-radius: 4px;
  background: var(--classic-surface, #fff);
}

.item {
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.55em 0.75em;
  border-bottom: 1px solid var(--classic-border-subtle, #f0f0f0);
  line-height: 1.35;
}

.checklist .item:last-child {
  border-bottom: none;
}

.message_input {
  width: 100%;
  min-height: 140px;
  background-color: var(--classic-surface, Field);
  color: var(--classic-text, FieldText);
  border: 1px solid var(--classic-border, #d0d0d0);
  border-radius: 4px;
  padding: 0.6em;
  font-family: inherit;
  resize: vertical;
}

@media (prefers-color-scheme: dark) {
  .message_input::placeholder {
    color: var(--classic-text-muted, revert);
  }
}

.member_name {
  font-weight: 500;
}

.member_email,
.member_teams {
  color: var(--classic-text-secondary, #666);
  font-size: 0.9em;
}

.title_row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1em;
}

.controls {
  display: flex;
  gap: 0.4em;
  flex-wrap: wrap;
}

.button.small {
  font-size: 0.78rem;
  padding: 0.35em 0.6em;
}

.toggle_button {
  display: inline-flex;
  align-items: center;
  gap: 0.4em;
}

.chevron {
  display: inline-block;
  transition: transform 120ms ease-in-out;
}

.chevron.expanded {
  transform: rotate(90deg);
}

.field_row {
  margin-top: 0.7em;
}

.status {
  font-size: 0.9em;
  margin-top: 0.8em;
  margin-bottom: 0;
}

.status.error {
  color: var(--classic-danger, #b00020);
}

.status.success {
  color: var(--classic-success, #0b7a31);
}

.buttons {
  margin-top: 1em;
}
</style>
